import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDealers } from '../context/DealersContext';
import Loader from '../component/loader';
import { Alert, Container } from 'react-bootstrap';

function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { dealers } = useDealers();
  const navigate = useNavigate();
  const location = useLocation();

  // Define route groups
  const dealerOnlyRoutes = ['/my-team']; // Routes only for dealers
  const dealerAccountRoutes = ['/my-account']; // Routes only for dealers
  const dealerAndEmployeeRoutes = ['/my-cars', '/add-car', '/edit-car']; // Routes for both dealers and employees

  const isDealer = user?.role === 'Dealer';
  const isDealerEmployee = user?.role === 'DealerEmployee';

  // Check if employee has approved access in any dealer's employees list
  const hasApprovedDealerAccess =
    isDealerEmployee &&
    dealers?.some(
      (dealer) => dealer.employees?.[user.uid]?.status === 'approved'
    );

  // Either a dealer or an employee with approved access
  const hasDealerAccess = isDealer || hasApprovedDealerAccess;

  useEffect(
    function () {
      if (!isLoading) {
        if (!isAuthenticated) {
          console.log('Not authenticated, redirecting to login');
          navigate('/login');
          return;
        }

        const currentPath = location.pathname;
        //console.log('Checking access for:', {
        //  path: currentPath,
        //  role: user?.role,
        //  hasDealerAccess,
        //  isDealer,
        //  isDealerEmployee,
        //  hasApprovedDealerAccess
        //});

        // First check dealer-only routes
        if (dealerOnlyRoutes.some((route) => currentPath.startsWith(route))) {
          if (!isDealer) {
            console.log(
              'Redirecting: dealer-only route accessed by non-dealer'
            );
            navigate('/');
            return;
          }
        }

        // Then check dealer account routes
        if (
          dealerAccountRoutes.some((route) => currentPath.startsWith(route))
        ) {
          if (!isDealer) {
            console.log(
              'Redirecting: dealer account route accessed by non-dealer'
            );
            navigate('/');
            return;
          }
        }

        // Check dealer/employee routes
        if (
          dealerAndEmployeeRoutes.some((route) => currentPath.startsWith(route))
        ) {
          // If they're an employee without approved access, redirect to my-access
          if (isDealerEmployee && !hasApprovedDealerAccess) {
            console.log('Redirecting employee to accept invitation first');
            navigate('/my-access');
            return;
          }
          // If they're neither a dealer nor an approved employee
          if (!hasDealerAccess) {
            console.log(
              'Redirecting: dealer/employee route accessed by unauthorized user'
            );
            navigate('/');
            return;
          }
        }

        // console.log('Access granted:', {
        //   path: currentPath,
        //   role: user?.role,
        //   hasDealerAccess,
        //   isDealer,
        //   isDealerEmployee,
        //   hasApprovedDealerAccess
        // });
      }
    },
    [
      isAuthenticated,
      isLoading,
      navigate,
      location,
      user,
      hasDealerAccess,
      isDealer,
      isDealerEmployee,
      hasApprovedDealerAccess,
      dealers,
    ]
  );

  if (isLoading) {
    return <Loader />;
  }

  // Show access denied message for non-dealers trying to access dealer-only routes
  if (
    isAuthenticated &&
    !isDealer &&
    dealerOnlyRoutes.some((route) => location.pathname.startsWith(route))
  ) {
    return (
      <div className="site-content">
        <section className="section-ptb">
          <Container>
            <Alert variant="warning">
              You need to be registered as a dealer to access this page.
            </Alert>
          </Container>
        </section>
      </div>
    );
  }

  // Show access denied message for non-dealers trying to access dealer account routes
  if (
    isAuthenticated &&
    !isDealer &&
    dealerAccountRoutes.some((route) => location.pathname.startsWith(route))
  ) {
    return (
      <div className="site-content">
        <section className="section-ptb">
          <Container>
            <Alert variant="warning">
              You need to be registered as a dealer to access this page.
            </Alert>
          </Container>
        </section>
      </div>
    );
  }

  // Show special message for employees who need to accept their invitation
  if (
    isAuthenticated &&
    isDealerEmployee &&
    !hasApprovedDealerAccess &&
    dealerAndEmployeeRoutes.some((route) => location.pathname.startsWith(route))
  ) {
    return (
      <div className="site-content">
        <section className="section-ptb">
          <Container>
            <Alert variant="info">
              Please accept your dealer invitation in the My Access page before
              accessing this feature.
            </Alert>
          </Container>
        </section>
      </div>
    );
  }

  return isAuthenticated ? children : null;
}

export default ProtectedRoute;
